import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const generateCrossMatchPDF = (
  crossMatchData: any,
  formatDate: (date: string) => string,
  profile: any,
  hospitalName: string
) => {
  if (!crossMatchData) {
    console.error("No cross match data available");
    return null;
  }

  const doc = new jsPDF();
  let finalY = 0;
  const pageWidth = doc.internal.pageSize.width;
  const margin = 15;
  const capitalizeFirstLetter = (str: string | undefined): string => {
    if (!str) return "N/A";
    const cleanedStr = String(str).trim();
    if (!cleanedStr || cleanedStr === "-" || cleanedStr.toUpperCase() === "N/A") return "N/A";
    return cleanedStr.charAt(0).toUpperCase() + cleanedStr.slice(1);
  };

  const safeFormatDate = (value: any) => {
    if (!value) return "N/A";
    try {
      const formatted = formatDate(value);
      if (!formatted || formatted === "-" || formatted.toUpperCase() === "N/A") return "N/A";
      return formatted;
    } catch {
      return "N/A";
    }
  };

  const formatCrossMatchDateTime = (value: any) => {
    if (!value) return "N/A";
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return safeFormatDate(value);
      const datePart = safeFormatDate(value);
      // Avoid duplicating time when formatDate already includes it.
      if (/\d{1,2}:\d{2}/.test(datePart)) return datePart;
      const timePart = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      return `${datePart} ${timePart}`.trim();
    } catch {
      return safeFormatDate(value);
    }
  };

  const bloodProduct =
    crossMatchData.bagComponent?.component ||
    (Array.isArray(crossMatchData.bagData?.bloodCell)
      ? crossMatchData.bagData.bloodCell.join(", ")
      : crossMatchData.bagData?.bloodCell) ||
    crossMatchData.bloodProduct ||
    "N/A";

  const bloodQty =
    crossMatchData.bloodQty ||
    crossMatchData.bagComponent?.quantity ||
    crossMatchData.bagData?.bloodQty ||
    "";

  const donorBloodGroup =
    crossMatchData.bagData?.bloodGroup ||
    crossMatchData.donorData?.bloodGroup ||
    "N/A";

  const donorRh =
    crossMatchData.bagData?.rhFactor ||
    crossMatchData.donorData?.rhFactor ||
    "";

  const expireDate =
    crossMatchData.bagComponent?.expiryDate ||
    crossMatchData.bagData?.expiryDate ||
    crossMatchData.expiryDate;

  const pageHeaderData = crossMatchData.pageHeaderData || {};
  const headerLogoUrl = pageHeaderData.logo || "";
  const headerTitle = pageHeaderData.title || "";
  const headerAddress = pageHeaderData.address || "";
  const headerPhone = pageHeaderData.phone || "";

  if (headerLogoUrl || headerTitle || headerAddress || headerPhone) {
    const headerY = 10;
    const logoWidth = 20;
    const logoHeight = 20;
    const logoTextSpacing = 0;

    let titleWidth = 0;
    let addressWidth = 0;

    if (headerTitle) {
      doc.setFont("times", "bold").setFontSize(18);
      titleWidth = doc.getTextWidth(headerTitle.toUpperCase());
    }

    if (headerAddress || headerPhone) {
      doc.setFont("times", "bold").setFontSize(8);
      const addressText = `${headerAddress?.trim() || ""} (Ph: ${headerPhone?.trim() || ""})`;
      addressWidth = doc.getTextWidth(addressText);
    }

    const maxTextWidth = Math.max(titleWidth, addressWidth);
    const totalHeaderWidth = (headerLogoUrl ? logoWidth + logoTextSpacing : 0) + maxTextWidth;
    const headerStartX = (pageWidth - totalHeaderWidth) / 2.2;

    if (headerLogoUrl) {
      try {
        const logoX = headerStartX;
        doc.addImage(headerLogoUrl, "PNG", logoX, headerY, logoWidth, logoHeight);
      } catch (error) {
        console.error("Error adding logo:", error);
      }
    }

    if (headerTitle || headerAddress || headerPhone) {
      const textStartX = headerLogoUrl ? headerStartX + logoWidth + logoTextSpacing : headerStartX;

      if (headerTitle) {
        doc.setFont("times", "bold").setFontSize(18);
        doc.text(headerTitle.toUpperCase(), textStartX, headerY + 10);
        doc.setDrawColor(0).setLineWidth(0.8);
        doc.line(textStartX, headerY + 12, textStartX + titleWidth + 2, headerY + 12);
      }

      if (headerAddress || headerPhone) {
        doc.setFont("times", "bold").setFontSize(8);
        const addressText = `${headerAddress?.trim() || ""} (Ph: ${headerPhone?.trim() || ""})`;
        doc.text(addressText, textStartX, headerY + 17);
        doc.setDrawColor(0).setLineWidth(0.1);
        doc.line(textStartX, headerY + 18, textStartX + addressWidth - 23, headerY + 18);
      }
    }
    finalY = 50;
  }

  doc.setFont("helvetica", "bold").setFontSize(14);
  doc.text("BLOOD GROUP AND CROSS MATCH REPORT", pageWidth / 2, finalY, { align: "center" });
  finalY += 5;

  autoTable(doc, {
    startY: finalY,
    body: [
      ["Bag No:", { content: capitalizeFirstLetter(crossMatchData.bagNo), styles: { fontStyle: "bold" } }, "Slip No:", { content: capitalizeFirstLetter(crossMatchData.slipNo), styles: { fontStyle: "bold" } }],
      ["Collection Date:", { content: capitalizeFirstLetter(safeFormatDate(crossMatchData.bagData?.collectionDate)), styles: { fontStyle: "bold" } }, "Expire Date:", { content: capitalizeFirstLetter(safeFormatDate(expireDate)), styles: { fontStyle: "bold" } }],
      ["Cross Match Date:", { content: capitalizeFirstLetter(formatCrossMatchDateTime(crossMatchData.crossMatchDate)), styles: { fontStyle: "bold" } }],
    ],
    theme: "plain",
    styles: { fontSize: 10, cellPadding: 1, overflow: "linebreak" },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 35 }, 2: { fontStyle: "bold", cellWidth: 35 } },
  });
  finalY = doc.lastAutoTable?.finalY || finalY + 30;

  // --- Patient & Donor Details Table ---
  doc.setFont("helvetica", "bold").setFontSize(12);
  doc.text("Patient & Donor Details", margin, finalY + 5);
  finalY += 10;

  autoTable(doc, {
    startY: finalY,
    body: [
      ["Name:", { content: crossMatchData.patientDetails?.patientName || "N/A", styles: { fontStyle: "bold" } }, "MRN:", { content: crossMatchData.patientDetails?.mrn || "N/A", styles: { fontStyle: "bold" } }],


       ["Phone No", { content: crossMatchData.patientDetails?.phonNumber||"", styles: { fontStyle: "bold" } }, "Ref_MRN:", { content: crossMatchData.patientDetails?.ref_mrn || "", styles: { fontStyle: "bold" } }],



      ["Gender:", { content: crossMatchData.patientDetails?.gender || "N/A", styles: { fontStyle: "bold" } }, "DOB:", { content: crossMatchData.patientDetails?.dob || "N/A", styles: { fontStyle: "bold" } }],
      ["Patient Blood Group:", { content: crossMatchData.slipData?.bloodGroup || "N/A", styles: { fontStyle: "bold" } }, "Rh:", { content: crossMatchData.slipData?.rhFactor === "+" ? "POSITIVE" : "NEGATIVE", styles: { fontStyle: "bold" } }],
      ["Donor Blood Group:", { content: donorBloodGroup, styles: { fontStyle: "bold" } }, "Rh:", { content: donorRh === "+" ? "POSITIVE" : donorRh === "-" ? "NEGATIVE" : (donorRh || "N/A"), styles: { fontStyle: "bold" } }],
      ["Blood Product:", { content: bloodProduct, styles: { fontStyle: "bold" } }],
      ["Blood Quantity:", { content: bloodQty ? `${bloodQty} ml` : "N/A", styles: { fontStyle: "bold" } }],
      ["Hospital:", { content: hospitalName, styles: { fontStyle: "bold" } }, "Performed By:", { content: crossMatchData?.performedBy || "-", styles: { fontStyle: "bold" } }],
    ],
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 1, overflow: "linebreak" },
  });
  finalY = doc.lastAutoTable?.finalY || finalY + 30;

  doc.setFont("helvetica", "bold").setFontSize(12);
  doc.text("CROSS MATCH NOTES", margin, finalY + 5);
  finalY += 10;

  doc.setFont("helvetica", "normal").setFontSize(10);
  const crossMatchNotes = [
    "1. This Blood Bag Has Been Donated By a Healthy Voluntary Donor.",
    "2. This blood product has been screened for HBsAg, HCV, HIV, MP & VDRL (Syphilis).",
    "3. Donor's Red Blood Cell are COMPATIBLE with the patient's serum at room temperature, 37°C in Saline Bovine, Albumin and Coombs reagents or by gel card method.",
    "4. Hemolysis checked.",
    "5. Blood Group and Cross Matching is performed on the Blood sample provided to Blood Bank.",
    // `6. Store between 2 - 6°C.`,
    `6. ${crossMatchData.bagComponent?.storageMessage || "Store between 2 - 6°C"}.`,
    "7. This Cross Match Report is only valid for (04) Four Days from the date of Cross Match performed.",
    "8. *This is a computer-generated report, requires no signature or stamp.*",
  ];

  doc.text(crossMatchNotes, margin, finalY, { maxWidth: pageWidth - (margin * 2) - 50 });
  const notesHeight = doc.getTextDimensions(crossMatchNotes.join("\n"), { maxWidth: pageWidth - (margin * 2) - 50 }).h;

  const remarksText = crossMatchData.remarks || "Good";
  const remarksBoxX = pageWidth - margin - 45;
  const remarksBoxY = finalY - 5;
  const remarksBoxHeight = notesHeight > 40 ? notesHeight + 10 : 40;
  doc.setDrawColor(0).setLineWidth(0.5);
  doc.rect(remarksBoxX, remarksBoxY, 45, remarksBoxHeight);
  doc.setFont("helvetica", "bold").setFontSize(10);
  doc.text("Remarks:", remarksBoxX + 2, remarksBoxY + 5);
  doc.setFont("helvetica", "normal").setFontSize(10);
  doc.text(remarksText, remarksBoxX + 2, remarksBoxY + 10, { maxWidth: 41 });
  finalY += notesHeight + 5;

  doc.setFont("helvetica", "bold").setFontSize(12);
  doc.text("BEFORE TRANSFUSION, PLEASE ENSURE THE FOLLOWING:", margin, finalY + 5);
  finalY += 10;

  doc.setFont("helvetica", "normal").setFontSize(10);
  const precautions = [
    "1. Check Blood Bag No & Group with Cross Match Report.",
    "2. Blood product should be transfused at room temperature.",
    "3. Packed cells should be transfused within 3 to 4 hours of issuance.",
    "4. Transfusion should not be given with a needle thinner than 18 gauge.",
    "5. Blood product should be stored in a Blood Bank Refrigerator / Freezer / Platelets Shaker, etc.",
    "6. FFP once thawed cannot be frozen again, rather it should be discarded.",
  ];
  doc.text(precautions, margin, finalY, { maxWidth: 190 });
  finalY += doc.getTextDimensions(precautions.join("\n"), { maxWidth: 190 }).h + 5;

  finalY += 10;

  doc.setFont("helvetica", "bold").setFontSize(12);
  doc.text("NO RETURN, NO REFUND", pageWidth / 2, finalY, { align: "center" });

  doc.line(10, finalY + 5, pageWidth - 10, finalY + 5);
  finalY += 10;

  doc.setFont("helvetica", "bold").setFontSize(10);
  doc.text("IN CASE OF ADVERSE REACTION, PLEASE FILL THE FORM BELOW:", margin, finalY);
  finalY += 5;

  doc.setFont("helvetica", "normal").setFontSize(10);
  const adverseReactionFields = [
    "Transfusion Date: ____/____/____",
    "Transfusion Time (Start): _______________",
    "Time reaction noticed: _______________",
    "Volume Transfused (ml): _______________",
    "Transfused By: _______________",
    "Contact: _______________",
  ];
  doc.text(adverseReactionFields, margin, finalY, { maxWidth: 190 });

  return doc;
};